import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  DatePicker,
  Drawer,
  Input,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import {
  ExportOutlined,
  LinkOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import dayjsTZ from "../../../../utils/dayjs";
import { pickerToDayKey } from "../../../../utils/timezone/dayBounds";
import { getCompanyTimezone } from "../../../../utils/timezone/teamTimezone";
import MondayButton from "../../../commonComponents/MondayButton/MondayButton";
import {
  filterRequestsForEmail,
  gmailOpenUrl,
  matchScore,
  normalizeEmail,
} from "./inboxHelpers";

const { Text, Paragraph, Title } = Typography;
const { RangePicker } = DatePicker;

const InboxReviewDrawer = ({
  active,
  onClose,
  onChange,
  onConvert,
  onLinkExisting,
  linking,
}) => {
  const { employees } = useSelector((state) => state.employeesList);
  const { employeeRequests } = useSelector((state) => state.employeeRequests);
  const { isDarkMode } = useSelector((state) => state.usersList);

  const ai = active?.ai || {};
  const [linkEmail, setLinkEmail] = useState("");
  const [linkDates, setLinkDates] = useState(null);

  useEffect(() => {
    if (!active) return;
    setLinkEmail(normalizeEmail(active.fromEmail) || "");
    if (active.ai?.startDate && active.ai?.endDate) {
      setLinkDates([
        dayjsTZ(active.ai.startDate),
        dayjsTZ(active.ai.endDate),
      ]);
    } else {
      setLinkDates(null);
    }
  }, [active?.queueId]);

  const employeeOptions = useMemo(
    () =>
      (employees || [])
        .filter((e) => e.employeeStatus === "Aktiv" && e.userSub)
        .map((e) => ({
          value: e.employeeId,
          label: `${e.employeeFirstName} ${e.employeeLastName}`,
          emp: e,
        })),
    [employees]
  );

  const gmailUrl = gmailOpenUrl(active?.threadId, active?.gmailMessageId);

  const candidates = useMemo(() => {
    if (!active) return [];
    const email =
      linkEmail ||
      normalizeEmail(active.fromEmail) ||
      "";
    const startDate =
      linkDates?.[0]
        ? pickerToDayKey(linkDates[0], getCompanyTimezone())
        : ai.startDate || null;
    const endDate =
      linkDates?.[1]
        ? pickerToDayKey(linkDates[1], getCompanyTimezone())
        : ai.endDate || null;

    const list = filterRequestsForEmail(employeeRequests, employees, {
      email,
      startDate,
      endDate,
      employeeId: ai.employeeMatch?.employeeId || active.employeeId || null,
    });

    return list
      .map((r) => ({ request: r, score: matchScore(r, ai) }))
      .sort((a, b) => b.score - a.score);
  }, [
    active,
    ai,
    employeeRequests,
    employees,
    linkEmail,
    linkDates,
  ]);

  if (!active) return null;

  const periods = Array.isArray(ai.periods) ? ai.periods : [];

  return (
    <Drawer
      open={!!active}
      onClose={onClose}
      width={480}
      destroyOnClose
      title={
        <Space>
          <MailOutlined />
          Rishiko email → kërkesë
        </Space>
      }
      className={`planer-inbox-review ${isDarkMode ? "is-dark" : ""}`}
    >
      <div className="inbox-review">
        <section className="inbox-review__email">
          <div className="inbox-review__email-head">
            <Title level={5} className="inbox-review__subject">
              {active.subject || "(pa subjekt)"}
            </Title>
            {gmailUrl ? (
              <Button
                type="link"
                icon={<ExportOutlined />}
                href={gmailUrl}
                target="_blank"
                rel="noreferrer"
              >
                Hap në Gmail
              </Button>
            ) : null}
          </div>
          <Text type="secondary" className="inbox-review__meta">
            Nga {active.fromEmail}
            {active.receivedAt
              ? ` · ${dayjsTZ(active.receivedAt).format("DD/MM/YYYY HH:mm")}`
              : ""}
          </Text>
          <pre className="inbox-review__body">
            {active.bodyText || active.snippet || "—"}
          </pre>
        </section>

        <section className="inbox-review__form">
          <Text className="inbox-review__label">Punonjësi (nga email)</Text>
          <Select
            showSearch
            optionFilterProp="label"
            style={{ width: "100%" }}
            placeholder="Zgjidh punonjësin"
            value={ai.employeeMatch?.employeeId || active.employeeId}
            options={employeeOptions}
            onChange={(employeeId, opt) => {
              const emp = opt.emp;
              onChange({
                ...active,
                employeeId,
                ai: {
                  ...ai,
                  employeeMatch: {
                    employeeId: emp.employeeId,
                    userSub: emp.userSub,
                    name: `${emp.employeeFirstName} ${emp.employeeLastName}`,
                    confidence: 1,
                  },
                },
              });
            }}
          />

          <Text className="inbox-review__label">Lloji</Text>
          <Select
            style={{ width: "100%" }}
            value={ai.requestType || "Pushime"}
            options={[
              { value: "Pushime", label: "Pushime" },
              { value: "Dite Personale", label: "Ditë Personale" },
              { value: "Leje lindje", label: "Leje lindje" },
              { value: "Raport mjekësor", label: "Raport mjekësor" },
            ]}
            onChange={(requestType) =>
              onChange({ ...active, ai: { ...ai, requestType } })
            }
          />

          <Text className="inbox-review__label">Datat e kërkesës</Text>
          <RangePicker
            style={{ width: "100%" }}
            format="DD/MM/YYYY"
            value={
              ai.startDate && ai.endDate
                ? [dayjsTZ(ai.startDate), dayjsTZ(ai.endDate)]
                : null
            }
            onChange={(vals) => {
              if (!vals) return;
              onChange({
                ...active,
                ai: {
                  ...ai,
                  startDate: pickerToDayKey(vals[0], getCompanyTimezone()),
                  endDate: pickerToDayKey(vals[1], getCompanyTimezone()),
                },
              });
            }}
          />
          {periods.length > 1 ? (
            <div className="inbox-review__periods">
              {periods.map((p, i) => (
                <Tag key={`${p.startDate}-${i}`} color="cyan">
                  {p.startDate} → {p.endDate}
                </Tag>
              ))}
            </div>
          ) : null}

          <Text className="inbox-review__label">Përshkrimi</Text>
          <Input.TextArea
            rows={3}
            value={ai.description || ""}
            onChange={(e) =>
              onChange({
                ...active,
                ai: { ...ai, description: e.target.value },
              })
            }
            placeholder="Përshkrimi i kërkesës"
          />

          <Space wrap className="inbox-review__actions">
            <MondayButton
              className="mondayButtonYellow"
              onClick={() => onConvert(active)}
            >
              Konverto (Pending)
            </MondayButton>
            <MondayButton
              className="mondayButtonGreen"
              onClick={() => onConvert(active, { approve: true })}
            >
              Konverto & Aprovo
            </MondayButton>
          </Space>
        </section>

        <section className="inbox-review__link">
          <Title level={5}>Lidh me kërkesë ekzistuese</Title>
          <Paragraph type="secondary" className="inbox-review__hint">
            Filtro sipas email-it të punonjësit dhe datave të lejes për ta
            shoqëruar këtë email me një kërkesë tashmë në sistem (pa krijuar
            dublikatë).
          </Paragraph>
          <Input
            allowClear
            placeholder="Filtro email / emër…"
            value={linkEmail}
            onChange={(e) => setLinkEmail(e.target.value)}
            style={{ marginBottom: 8 }}
          />
          <RangePicker
            style={{ width: "100%", marginBottom: 12 }}
            format="DD/MM/YYYY"
            placeholder={["Data fillimi", "Data mbarimi"]}
            value={linkDates}
            onChange={setLinkDates}
          />
          <div className="inbox-review__candidates">
            {candidates.length === 0 ? (
              <Text type="secondary">Nuk u gjet asnjë kërkesë me këta filtra.</Text>
            ) : (
              candidates.map(({ request: r, score }) => {
                const exact = score >= 90;
                return (
                  <div
                    key={r.requestId}
                    className={`inbox-candidate ${exact ? "is-exact" : ""}`}
                  >
                    <div>
                      <strong>
                        {r.given_name} {r.family_name}
                      </strong>
                      <div className="inbox-candidate__meta">
                        {r.requestType} · {r.requestStatus} ·{" "}
                        {dayjsTZ(r.requestPeriod[0]).format("DD/MM/YY")} –{" "}
                        {dayjsTZ(r.requestPeriod.at(-1)).format("DD/MM/YY")}
                      </div>
                      {exact ? (
                        <Tag color="green">Përputhje e saktë</Tag>
                      ) : score > 0 ? (
                        <Tag>Ngjashmëri {Math.min(100, score)}</Tag>
                      ) : null}
                    </div>
                    <Button
                      type="primary"
                      size="small"
                      icon={<LinkOutlined />}
                      loading={linking === r.requestId}
                      onClick={() => {
                        if (!r.requestId) {
                          message.warning("Kërkesë e pavlefshme");
                          return;
                        }
                        onLinkExisting(active, r);
                      }}
                    >
                      Lidh
                    </Button>
                  </div>
                );
              })
            )}
          </div>
        </section>
      </div>
    </Drawer>
  );
};

export default InboxReviewDrawer;
